import { useEffect, useState } from 'react';
import { ArrowRight, Sparkles, LayoutGrid, Loader2, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

export type NewProjectTool = 'staging' | 'floorplan';

interface NewProjectModalProps {
  open: boolean;
  isCreating: boolean;
  /**
   * Wenn gesetzt, ist das Tool festgelegt (z. B. „Neues Staging" / „Neuer
   * Grundriss" aus der Sidebar): die Tool-Auswahl entfällt, es wird nur der
   * Projektname abgefragt. Ohne Wert erscheint die volle Auswahl.
   */
  lockedTool?: NewProjectTool | null;
  onClose: () => void;
  onConfirm: (name: string, tool: NewProjectTool) => void;
}

const TOOL_CTA: Record<NewProjectTool, string> = {
  staging: 'Mit Staging starten',
  floorplan: 'Mit Grundriss starten',
};

const LOCKED_HEADING: Record<NewProjectTool, string> = {
  staging: 'Neues Staging',
  floorplan: 'Neuer Grundriss',
};

/**
 * „Neues Projekt": Projektname (Pflicht) + Auswahl Staging oder Grundriss.
 * Die Karten markieren nur die Auswahl; erstellt wird erst über den
 * adaptiven Bestätigen-Button darunter (verhindert Fehleingaben).
 *
 * Mit `lockedTool` wird die Auswahl übersprungen (nur Name), z. B. für die
 * Sidebar-Unterpunkte „Neues Staging" / „Neuer Grundriss".
 */
export const NewProjectModal: React.FC<NewProjectModalProps> = ({ open, isCreating, lockedTool = null, onClose, onConfirm }) => {
  const [name, setName] = useState('');
  const [selectedTool, setSelectedTool] = useState<NewProjectTool | null>(null);

  // Bei jedem Öffnen zurücksetzen — im Locked-Modus ist das Tool fix.
  useEffect(() => {
    if (open) setSelectedTool(lockedTool);
  }, [open, lockedTool]);

  if (!open) return null;

  const trimmed = name.trim();
  const nameValid = trimmed.length > 0;
  const effectiveTool = lockedTool ?? selectedTool;
  const canConfirm = nameValid && effectiveTool !== null && !isCreating;

  const confirm = () => {
    if (!canConfirm || !effectiveTool) return;
    onConfirm(trimmed, effectiveTool);
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={() => !isCreating && onClose()}
    >
      <div
        className="w-full max-w-lg rounded-2xl border border-border bg-card p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold text-foreground">
              {lockedTool ? LOCKED_HEADING[lockedTool] : 'Neues Projekt'}
            </h2>
            <p className="mt-0.5 text-sm text-muted-foreground">
              {lockedTool
                ? 'Benenne dein Projekt — danach geht es direkt weiter.'
                : 'Benenne das Projekt und wähle, womit du starten willst.'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isCreating}
            className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50"
            aria-label="Schließen"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Projektname (Pflicht) */}
        <div className="mb-5">
          <Label htmlFor="new-project-name" className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
            Projektname <span className="text-primary">*</span>
          </Label>
          <Input
            id="new-project-name"
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') confirm();
            }}
            placeholder="z. B. Waldstraße 8"
            maxLength={120}
            disabled={isCreating}
            className={cn('mt-2', !nameValid && 'border-primary/60 focus-visible:ring-primary')}
          />
        </div>

        {/* Auswahl: Staging vs. Grundriss (nur markieren, nicht abschicken).
            Im Locked-Modus (Sidebar-Unterpunkte) entfällt die Auswahl. */}
        {!lockedTool && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <button
            type="button"
            onClick={() => setSelectedTool('staging')}
            disabled={isCreating}
            aria-pressed={selectedTool === 'staging'}
            className={cn(
              'flex flex-col items-center gap-2 rounded-xl border-2 p-5 text-center transition-all',
              selectedTool === 'staging'
                ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                : 'border-border hover:border-primary/50 hover:bg-primary/5',
              'disabled:cursor-not-allowed disabled:opacity-50',
            )}
          >
            <span
              className={cn(
                'flex h-12 w-12 items-center justify-center rounded-full transition-colors',
                selectedTool === 'staging' ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground',
              )}
            >
              <Sparkles className="h-6 w-6" />
            </span>
            <span className={cn('text-base font-bold', selectedTool === 'staging' ? 'text-primary' : 'text-foreground')}>
              Staging
            </span>
            <span className="text-xs text-muted-foreground">Räume leeren &amp; einrichten</span>
          </button>

          <button
            type="button"
            onClick={() => setSelectedTool('floorplan')}
            disabled={isCreating}
            aria-pressed={selectedTool === 'floorplan'}
            className={cn(
              'flex flex-col items-center gap-2 rounded-xl border-2 p-5 text-center transition-all',
              selectedTool === 'floorplan'
                ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                : 'border-border hover:border-primary/50 hover:bg-primary/5',
              'disabled:cursor-not-allowed disabled:opacity-50',
            )}
          >
            <span
              className={cn(
                'flex h-12 w-12 items-center justify-center rounded-full transition-colors',
                selectedTool === 'floorplan' ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground',
              )}
            >
              <LayoutGrid className="h-6 w-6" />
            </span>
            <span className={cn('text-base font-bold', selectedTool === 'floorplan' ? 'text-primary' : 'text-foreground')}>
              Grundriss
            </span>
            <span className="text-xs text-muted-foreground">2D- &amp; 3D-Grundriss erstellen</span>
          </button>
        </div>
        )}

        {/* Adaptiver Bestätigen-Button */}
        <button
          type="button"
          onClick={confirm}
          disabled={!canConfirm}
          className={cn(
            'mt-5 flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-bold transition-all',
            canConfirm
              ? 'bg-primary text-primary-foreground shadow-sm hover:bg-primary/90'
              : 'cursor-not-allowed bg-muted text-muted-foreground',
          )}
        >
          {isCreating ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Projekt wird erstellt…
            </>
          ) : (
            <>
              {effectiveTool ? TOOL_CTA[effectiveTool] : 'Programm wählen'}
              {effectiveTool && <ArrowRight className="h-4 w-4" />}
            </>
          )}
        </button>

        {!nameValid && (
          <p className="mt-3 text-center text-xs text-muted-foreground">
            Bitte zuerst einen Projektnamen eingeben.
          </p>
        )}
      </div>
    </div>
  );
};
